import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Sun, Moon, User, LogOut, Settings, LayoutDashboard, FileText, FolderOpen, Sparkles, Chrome } from 'lucide-react';

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50" data-testid="navbar">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-heading text-sm font-bold">LA</span>
            </div>
            <span className="font-heading text-lg tracking-tight hidden sm:inline">LIFE ADMIN</span>
          </Link>

          {/* Navigation Links - Only show when logged in */}
          {user && (
            <div className="hidden md:flex items-center gap-1">
              <Link to="/dashboard">
                <Button 
                  variant={isActive('/dashboard') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="uppercase text-xs tracking-wider"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/documents">
                <Button 
                  variant={isActive('/documents') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="uppercase text-xs tracking-wider"
                  data-testid="nav-documents"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Documents
                </Button>
              </Link>
              <Link to="/applications">
                <Button 
                  variant={isActive('/applications') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="uppercase text-xs tracking-wider"
                  data-testid="nav-applications"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Applications
                </Button>
              </Link>
              <Link to="/packs">
                <Button 
                  variant={isActive('/packs') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="uppercase text-xs tracking-wider"
                  data-testid="nav-packs"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Packs
                </Button>
              </Link>
              <Link to="/extension">
                <Button 
                  variant={isActive('/extension') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="uppercase text-xs tracking-wider relative"
                  data-testid="nav-extension"
                >
                  <Chrome className="w-4 h-4 mr-2" />
                  Extension
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[8px] px-1 py-0 font-bold tracking-wider">NEW</span>
                </Button>
              </Link>
            </div>
          )}

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="border border-border"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline text-xs uppercase tracking-wider">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    <p className="text-xs text-primary mt-1 uppercase">{user.subscription_tier} Plan</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/fill')} data-testid="menu-fill-helper">
                    <FileText className="w-4 h-4 mr-2" />
                    Fill Helper (fallback)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={login} 
                className="brutalist-shadow-sm uppercase text-xs tracking-wider"
                data-testid="login-button"
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
